import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
} from '@angular/core';
import { AuthModule, OidcSecurityService } from 'angular-auth-oidc-client';
import { authConfig } from '../utils/auth.config';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { registerLocaleData } from '@angular/common';
import localeGermany from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeGermanyExtra from '@angular/common/locales/extra/de';
import localeEnglishExtra from '@angular/common/locales/extra/en';
import { AuthService } from './shared/services/auth.service';
import { AuthInterceptorService } from './shared/services/auth-interceptor.service';
import { en_US, NZ_I18N } from 'ng-zorro-antd/i18n';
import { provideRouter, withHashLocation } from '@angular/router';
import { routes } from './app-routing';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideServiceWorker } from '@angular/service-worker';
import { TRANSLATE_HTTP_LOADER_CONFIG, TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NzMessageService } from 'ng-zorro-antd/message';

export function authInitializer(oidc: OidcSecurityService): () => Promise<void> {
  return async () => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    const errorDesc = params.get('error_description');

    // Handle cognito erros
    if (error || errorDesc) {
      let message = decodeURIComponent(errorDesc || error);
      message = message.replace(/\+/g, ' ').trim();

      const knownPrefixes = [
        'Invalid SAML response received',
        'PostAuthentication failed with error',
        'invalid_request',
        'unauthorized_client',
        'access_denied',
        'server_error',
      ];

      for (const prefix of knownPrefixes) {
        const regex = new RegExp(`^${prefix}[:\\-]?\\s*`, 'i');
        message = message.replace(regex, '');
      }

      if (!message || message.length < 3) {
        message = 'An authentication error occurred. Please try signing in again.';
      }
      const msg = inject(NzMessageService);
      msg.error(message, { nzDuration: 5000 });
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => {
        oidc.authorize();
      }, 5000);
      return;
    }

    const hasCode = params.has('code');
    const hasState = params.has('state');
    // Handle no state in url
    if (hasCode && !hasState) {
      window.history.replaceState({}, document.title, window.location.origin);
      oidc.authorize();
    } else {
      const { isAuthenticated } = await firstValueFrom(oidc.checkAuth());
      if (!isAuthenticated) {
        oidc.authorize();
      }
    }
  };
}

registerLocaleData(localeGermany, 'de-DE', localeGermanyExtra);
registerLocaleData(localeEn, 'en-EN', localeEnglishExtra);

export const appConfig: ApplicationConfig = {
  providers: [
    AuthService,
    provideAnimations(),
    provideRouter(routes, withHashLocation()),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: {
        prefix: '../../../assets/i18n/',
        suffix: '.json',
      },
    },
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useClass: TranslateHttpLoader,
        deps: [HttpClient],
      },
    }),
    importProvidersFrom([AuthModule.forRoot({ config: authConfig })]),
    provideAppInitializer(() => {
      const initializerFn = authInitializer(inject(OidcSecurityService));
      return initializerFn();
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptorService,
      multi: true,
    },
    {
      provide: NZ_I18N,
      useValue: en_US,
    },
    {
      provide: LOCALE_ID,
      useValue: 'en-GB', // 'de-DE' for Germany, 'fr-FR' for France ...
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
